import React, { useState, useEffect, useCallback, useRef } from "react";
import { Play, Eye, Heart, Sparkles, AlertCircle, FileText, Globe, Tv, ShieldCheck, Home, Search, User, Compass, ShieldAlert, Lock } from "lucide-react";
import { motion, AnimatePresence } from 'motion/react';
import { io } from "socket.io-client";
import { Video, Scraper, Settings, Page, getProxiedThumbnailUrl } from "./types";
import Header from "./components/Header";
import VideoGrid from "./components/VideoGrid";
import VideoDetailModal from "./components/VideoDetailModal";
import ScraperView from "./components/ScraperView";
import AdminPanel from "./components/AdminPanel";
import AlienXBackground from "./components/AlienXBackground";
import OmnitrixButton from "./components/OmnitrixButton";

export default function App() {
  // Portal raw state with robust offline caching
  const [videos, setVideos] = useState<Video[]>(() => {
    try {
      const cached = localStorage.getItem("cached_videos");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [shuffledVideos, setShuffledVideos] = useState<Video[]>([]);
  const [scrapers, setScrapers] = useState<Scraper[]>(() => {
    try {
      const cached = localStorage.getItem("cached_scrapers");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [pages, setPages] = useState<Page[]>(() => {
    try {
      const cached = localStorage.getItem("cached_pages");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [settings, setSettings] = useState<Settings>(() => {
    const defaultSettings = {
      siteName: "VIRAL STREAM",
      siteSlogan: "Unlimited Premium Video Streaming & Web Scraper Portal",
      bannerUrl: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1600"
    };
    try {
      const cached = localStorage.getItem("cached_settings");
      return cached ? JSON.parse(cached) : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  // UI States
  const [activeTab, setActiveTab] = useState("videos");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_videos");
      return cached ? JSON.parse(cached).length === 0 : true;
    } catch {
      return true;
    }
  });
  const [error, setError] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isCrawlingGlobal, setIsCrawlingGlobal] = useState(false);

  // Admin Auth States
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState("");
  const [isBlockedByAdmin, setIsBlockedByAdmin] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");

  // Security and Copy protection states
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  
  // Alien X Theme State
  const [isAlienXModeActivated, setIsAlienXModeActivated] = useState(false);

  useEffect(() => {
    const handleBlocked = (e: Event) => {
      setIsBlockedByAdmin(true);
      setBlockedMessage((e as CustomEvent).detail || "Your access has been suspended.");
    };
    window.addEventListener("portal-access-blocked", handleBlocked);
    return () => {
      window.removeEventListener("portal-access-blocked", handleBlocked);
    };
  }, []);

  // Copy Protection and Keyboard Shortcut Inhibitor Effect
  useEffect(() => {
    // Explicitly allow context menu, text selection, and copy-paste
    document.oncontextmenu = null;
    document.ondragstart = null;
    document.oncopy = null;
    document.oncut = null;
    document.onselectstart = null;
    document.onkeydown = null;
  }, []);

  // Removed devTools inspection guard as it was causing issues.

  // Auto-clear toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const lastDbVersionRef = useRef<number>(0);

  // Fetch Portal Data on Mount
  const fetchPortalData = useCallback(async (retryCount = 0) => {
    try {
      const res = await fetch("/api/portal-data");
      if (!res.ok) {
        throw new Error(`Server status ${res.status}`);
      }

      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error(`Expected JSON but received ${contentType || "HTML"}`);
      }

      const data = await res.json();
      if (!data || typeof data !== "object") {
        throw new Error("Invalid payload received from portal API");
      }
      
      setError(""); // Clear error on successful fetch

      if (data.dbVersion) {
        lastDbVersionRef.current = data.dbVersion;
      }

      const newVideos: Video[] = Array.isArray(data.videos) ? data.videos : [];
      setVideos(newVideos);
      setScrapers(Array.isArray(data.scrapers) ? data.scrapers : []);
      setPages(Array.isArray(data.pages) ? data.pages : []);
      if (data.settings && typeof data.settings === "object") {
        setSettings(data.settings);
      }
      if (data.isCrawling !== undefined) {
        setIsCrawlingGlobal(Boolean(data.isCrawling));
      }

      // Save to localStorage cache for offline-first resilience
      try {
        localStorage.setItem("cached_videos", JSON.stringify(newVideos));
        localStorage.setItem("cached_scrapers", JSON.stringify(data.scrapers || []));
        localStorage.setItem("cached_pages", JSON.stringify(data.pages || []));
        if (data.settings) {
          localStorage.setItem("cached_settings", JSON.stringify(data.settings));
        }
      } catch (storageErr) {
        console.warn("Could not save portal cache to localStorage", storageErr);
      }

      // If currently selected video was deleted by admin, clear selected video
      setSelectedVideo((prev) => {
        if (!prev) return null;
        const exists = newVideos.some((v) => v.id === prev.id);
        return exists ? prev : null;
      });

      return data;
    } catch (err: any) {
      // Auto-retry up to 3 times with progressive delay if the server is still booting or restarting
      if (retryCount < 3) {
        const delay = (retryCount + 1) * 1200;
        setTimeout(() => {
          fetchPortalData(retryCount + 1);
        }, delay);
        return;
      }

      console.warn("Portal data fetch temporarily unavailable, using offline cache:", err?.message || err);

      // Only set UI error if we have zero videos in memory and no cache
      setVideos((prev) => {
        if (prev.length === 0) {
          setError("Network slow or reconnecting. Retrying connection...");
        }
        return prev;
      });
    } finally {
      if (retryCount >= 2 || !error) {
        setLoading(false);
      }
    }
  }, [error]);

  // Real-time server sync listener removed to prevent automatic background refreshes during video playback

  useEffect(() => {
    // 1. Initial load
    fetchPortalData().then((data) => {
      if (!data) return;
      // 2. Query param handling for direct video sharing e.g. ?vid=vid_1
      const params = new URLSearchParams(window.location.search);
      const videoIdParam = params.get("vid");
      if (videoIdParam && data.videos) {
        const targetVideo = data.videos.find((v: Video) => v.id === videoIdParam);
        if (targetVideo) {
          setSelectedVideo(targetVideo);
        }
      }
    });

    // WebSockets for Real-time Auto Refresh without Reloading
    const socket = io(); // Defaults to same host
    socket.on("db_update", (data: any) => {
      if (data.videos) {
        setVideos(data.videos);
        // If a selected video was deleted, close the modal
        setSelectedVideo(prev => {
          if (prev && !data.videos.find((v: Video) => v.id === prev.id)) {
            return null; // Close modal
          }
          return prev;
        });
      }
      if (data.scrapers) setScrapers(data.scrapers);
      if (data.pages) setPages(data.pages);
      if (data.settings) setSettings(prev => ({ ...prev, ...data.settings }));
    });

    // 3. Admin session restore with strict server verification
    const savedToken = localStorage.getItem("admin_secret_token");
    if (savedToken) {
      fetch("/api/admin/verify", {
        headers: { "Authorization": `Bearer ${savedToken}` }
      })
        .then((res) => {
          if (res.ok) {
            setAdminToken(savedToken);
            setIsAdmin(true);
          } else {
            localStorage.removeItem("admin_secret_token");
            setAdminToken("");
            setIsAdmin(false);
          }
        })
        .catch(() => {
          localStorage.removeItem("admin_secret_token");
          setAdminToken("");
          setIsAdmin(false);
        });
    } else {
      setIsAdmin(false);
    }

    return () => {
      socket.disconnect();
    };
  }, [fetchPortalData]);

  // Global crawler polling effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isCrawlingGlobal) {
      // Poll every 15 seconds if active background crawl is running to smoothly populate newly crawled videos without overloading network
      interval = setInterval(() => {
        fetchPortalData();
      }, 15000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCrawlingGlobal, fetchPortalData]);

  // Helper to shuffle array
  const shuffleList = useCallback(<T,>(arr: T[]): T[] => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, []);

  // Ref to track initial load
  const isInitialLoadRef = useRef(true);

  // Maintain locked session video list while user is actively browsing on the site
  // Prevents auto-refresh/re-shuffling of videos while on home page or tag pages
  useEffect(() => {
    if (videos.length === 0) {
      setShuffledVideos([]);
      return;
    }

    setShuffledVideos((prev) => {
      if (prev.length === 0 || isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        return shuffleList(videos);
      }

      // Filter out deleted videos without changing order or adding shifting elements while browsing
      const currentValidIds = new Set(videos.map((v) => v.id));
      const cleanedPrev = prev.filter((v) => currentValidIds.has(v.id));
      return cleanedPrev;
    });
  }, [videos, shuffleList]);

  const lastHiddenTimeRef = useRef<number>(0);
  const selectedVideoRef = useRef<Video | null>(null);

  useEffect(() => {
    selectedVideoRef.current = selectedVideo;
  }, [selectedVideo]);

  // When user leaves website/tab and returns, or refreshes the website, refresh and shuffle all tags' videos
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        lastHiddenTimeRef.current = Date.now();
      } else if (document.visibilityState === "visible") {
        fetchPortalData().then((data) => {
          const freshVids = data && Array.isArray(data.videos) && data.videos.length > 0 ? data.videos : videos;
          if (lastHiddenTimeRef.current > 0 && freshVids.length > 0) {
            const timeAway = Date.now() - lastHiddenTimeRef.current;
            // If left the website/tab for at least 1.5 seconds, refresh & shuffle all tag videos on return
            if (timeAway > 1500 && !selectedVideoRef.current) {
              setShuffledVideos(shuffleList(freshVids));
            }
          }
        });
      }
    };

    const handleFocus = () => {
      if (document.visibilityState === "visible") {
        fetchPortalData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchPortalData, shuffleList, videos]);

  // Dynamically clean up and brand video titles
  const formattedVideos = React.useMemo(() => {
    const brandName = settings.siteName || "VIRAL STREAM";
    return videos.map((v) => {
      let title = v.title || "";
      // Strip "kama baba" (case-insensitive variations with possible spaces, hyphens, underscores)
      title = title.replace(/(?:kama[-_\s]*baba)/gi, "").trim();
      // Clean up extra spaces, dangling separators, or trailing dashes/pipes
      title = title.replace(/^[-\|\s]+|[-\|\s]+$/g, "").trim();
      title = title.replace(/\s+(?:by|with|at)$/gi, "").trim();
      title = title.replace(/^[-\|\s]+|[-\|\s]+$/g, "").trim();
      
      // Ensure the title ends with the brandName
      if (title && !title.toLowerCase().includes(brandName.toLowerCase())) {
        title = `${title} | ${brandName}`;
      }
      return {
        ...v,
        title
      };
    });
  }, [videos, settings.siteName]);

  const formattedShuffledVideos = React.useMemo(() => {
    const brandName = settings.siteName || "VIRAL STREAM";
    return shuffledVideos.map((v) => {
      let title = v.title || "";
      // Strip "kama baba" (case-insensitive variations with possible spaces, hyphens, underscores)
      title = title.replace(/(?:kama[-_\s]*baba)/gi, "").trim();
      // Clean up extra spaces, dangling separators, or trailing dashes/pipes
      title = title.replace(/^[-\|\s]+|[-\|\s]+$/g, "").trim();
      title = title.replace(/\s+(?:by|with|at)$/gi, "").trim();
      title = title.replace(/^[-\|\s]+|[-\|\s]+$/g, "").trim();
      
      // Ensure the title ends with the brandName
      if (title && !title.toLowerCase().includes(brandName.toLowerCase())) {
        title = `${title} | ${brandName}`;
      }
      return {
        ...v,
        title
      };
    });
  }, [shuffledVideos, settings.siteName]);

  // Display videos feed
  const displayVideos = formattedShuffledVideos.length > 0 ? formattedShuffledVideos : formattedVideos;

  // API Call helper for admin endpoints
  const getAdminHeaders = () => {
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${adminToken}`,
    };
  };

  // Login handler
  const handleAdminLogin = async (password: string): Promise<boolean> => {
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        const data = await res.json();
        setAdminToken(data.token);
        setIsAdmin(true);
        localStorage.setItem("admin_secret_token", data.token);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Login verification failed:", err);
      return false;
    }
  };

  // Logout handler
  const handleAdminLogout = () => {
    setAdminToken("");
    setIsAdmin(false);
    localStorage.removeItem("admin_secret_token");
    setActiveTab("videos");
  };

  // Video operations
  const handleAddVideo = async (payload: Omit<Video, "id" | "views" | "likes" | "createdAt">) => {
    const res = await fetch("/api/admin/videos", {
      method: "POST",
      headers: getAdminHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to publish video node");
    const data = await res.json();
    await fetchPortalData();
    return data.video;
  };

  const handleEditVideo = async (id: string, payload: Partial<Video>) => {
    const res = await fetch(`/api/admin/videos/${id}`, {
      method: "PUT",
      headers: getAdminHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to edit video node parameters");
    await fetchPortalData();
  };

  const handleDeleteVideo = async (id: string) => {
    const res = await fetch(`/api/admin/videos/${id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) throw new Error("Failed to delete video node");
    await fetchPortalData();
  };

  const handleDeleteAllVideos = async () => {
    const res = await fetch("/api/admin/videos", {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) throw new Error("Failed to delete all videos");
    await fetchPortalData();
  };

  const handleBulkDeleteVideos = async (keyword: string) => {
    const res = await fetch("/api/admin/videos/bulk-delete", {
      method: "POST",
      headers: {
        ...getAdminHeaders(),
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ keyword }),
    });
    if (!res.ok) throw new Error("Failed to bulk delete videos");
    const data = await res.json();
    await fetchPortalData();
    return data;
  };

  const handleDeleteMultipleVideos = async (ids: string[]) => {
    const res = await fetch("/api/admin/videos/delete-multiple", {
      method: "POST",
      headers: {
        ...getAdminHeaders(),
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error("Failed to delete selected videos");
    const data = await res.json();
    await fetchPortalData();
    return data;
  };

  // Scraper operations
  const handleAddScraper = async (payload: Omit<Scraper, "id" | "clicks">) => {
    const res = await fetch("/api/admin/scrapers", {
      method: "POST",
      headers: getAdminHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to configure web scraper preset");
    await fetchPortalData();
  };

  const handleEditScraper = async (id: string, payload: Partial<Scraper>) => {
    const res = await fetch(`/api/admin/scrapers/${id}`, {
      method: "PUT",
      headers: getAdminHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to save preset changes");
    await fetchPortalData();
  };

  const handleDeleteScraper = async (id: string) => {
    const res = await fetch(`/api/admin/scrapers/${id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) throw new Error("Failed to delete preset link");
    await fetchPortalData();
  };

  // Settings operations
  const handleUpdateSettings = async (payload: Partial<Settings> & { adminPassword?: string }) => {
    const res = await fetch("/api/admin/settings", {
      method: "POST",
      headers: getAdminHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to update general configuration parameters");
    
    // If admin password was updated, update token in state and storage
    if (payload.adminPassword) {
      setAdminToken(payload.adminPassword);
      localStorage.setItem("admin_secret_token", payload.adminPassword);
    }
    await fetchPortalData();
  };

  // Pages operations
  const handleUpdatePage = async (id: string, payload: Partial<Page>) => {
    const res = await fetch(`/api/admin/pages/${id}`, {
      method: "PUT",
      headers: getAdminHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to publish static page layout");
    await fetchPortalData();
  };

  const handleRenameCategory = async (oldName: string, newName: string) => {
    const res = await fetch("/api/admin/categories/rename", {
      method: "POST",
      headers: {
        ...getAdminHeaders(),
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ oldName, newName })
    });
    if (!res.ok) throw new Error("Failed to rename category");
    const data = await res.json();
    await fetchPortalData();
    return data;
  };

  const handleDeleteCategory = async (category: string, deleteVideos: boolean) => {
    const res = await fetch("/api/admin/categories/delete", {
      method: "POST",
      headers: {
        ...getAdminHeaders(),
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ category, deleteVideos })
    });
    if (!res.ok) throw new Error("Failed to delete category");
    const data = await res.json();
    await fetchPortalData();
    return data;
  };

  // Increment video likes locally and on server
  const handleLikeVideo = async (videoId: string) => {
    try {
      await fetch(`/api/videos/${videoId}/like`, { method: "POST" });
      setVideos((prevVideos) =>
        prevVideos.map((v) => (v.id === videoId ? { ...v, likes: v.likes + 1 } : v))
      );
      setSelectedVideo((prev) => (prev && prev.id === videoId ? { ...prev, likes: prev.likes + 1 } : prev));
    } catch (err) {
      console.error("Like recording failed:", err);
    }
  };

  // Increment scraper clicked stats
  const handleScraperPresetClick = async (scraperId: string) => {
    try {
      await fetch(`/api/scrapers/${scraperId}/click`, { method: "POST" });
      setScrapers((prevScrapers) =>
        prevScrapers.map((s) => (s.id === scraperId ? { ...s, clicks: s.clicks + 1 } : s))
      );
    } catch (err) {
      console.error("Preset click logging failed:", err);
    }
  };

  // Local Search Filter on Video Feed
  const filteredVideosBySearch = React.useMemo(() => {
    if (!searchQuery.trim()) return displayVideos;
    const query = searchQuery.toLowerCase();
    return displayVideos.filter((v) => {
      return (
        v.title.toLowerCase().includes(query) ||
        (v.description && v.description.toLowerCase().includes(query)) ||
        v.category.toLowerCase().includes(query)
      );
    });
  }, [displayVideos, searchQuery]);

  // Featured video selection (must not be a short video)
  const isShortVideo = (v: Video) => {
    if (v.category?.toLowerCase() === "shorts" || v.category?.toLowerCase() === "short") return true;
    if (!v.duration) return false;
    const parts = v.duration.split(":").map(Number);
    if (parts.length === 2) {
      const [m, s] = parts;
      return m === 0 || (m === 1 && s === 0);
    }
    if (parts.length === 1) {
      return parts[0] <= 60;
    }
    return false;
  };

  const handleAdminUnlock = () => {
    // Open a secure passcode prompt instead of bypassing authentication
    setAuthModalOpen(true);
    setAuthError("");
    setAuthPassword("");
  };

  const nonShortsVideos = formattedVideos.filter((v) => !isShortVideo(v));
  const featuredVideo = (settings.heroVideoId ? formattedVideos.find(v => v.id === settings.heroVideoId) : null) || nonShortsVideos.find((v) => v.category === "Trending") || nonShortsVideos[0] || formattedVideos[0];

  if (isBlockedByAdmin) {
    return (
      <div id="access-blocked-screen" className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center select-none">
        <div className="max-w-md w-full bg-slate-900 border border-slate-850 rounded-3xl p-8 shadow-2xl shadow-rose-950/20 space-y-6">
          <div className="w-16 h-16 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center justify-center text-rose-500 mx-auto animate-pulse">
            <AlertCircle className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl sm:text-2xl font-black text-slate-100 tracking-tight font-sans">Access Suspended</h1>
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed font-sans">
              {blockedMessage || "Your access to this streaming portal has been temporarily suspended by the administrator."}
            </p>
          </div>
          <div className="border-t border-slate-800 pt-4 text-[10px] text-slate-500 font-mono uppercase tracking-widest">
            IP Address tracked & logged. If you believe this is an error, please contact support.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        {!isAlienXModeActivated ? (
          <motion.div key="intro" className="fixed inset-0 z-50 bg-black" exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: "easeInOut" }}>
            <OmnitrixButton onActivate={() => {
              setIsAlienXModeActivated(true);
              if (settings.omnitrixFeaturedVideoId && videos.length > 0) {
                const feat = videos.find(v => v.id === settings.omnitrixFeaturedVideoId);
                if (feat) {
                  setSelectedVideo(feat);
                }
              }
            }} />
          </motion.div>
        ) : (
          <motion.div 
            key="portal-root"
            id="portal-root" 
            className="min-h-screen text-slate-100 flex flex-col font-sans relative bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <AlienXBackground />
          <div className="relative z-10 bg-black/60 min-h-screen flex flex-col backdrop-blur-sm">
      
      {/* Header element */}
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        settings={settings}
        pages={pages}
        isAdmin={isAdmin}
        onLogout={handleAdminLogout}
        onAdminUnlock={handleAdminUnlock}
      />

      {/* Main Content Arena */}
      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        
        {loading ? (
          /* Professional Skeleton Loading State */
          <div className="space-y-6 w-full animate-pulse">
            {/* Category Pills Skeleton */}
            <div className="flex flex-wrap gap-3 mb-8">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 w-24 bg-zinc-900 border border-zinc-800 rounded-full"></div>
              ))}
            </div>
            
            {/* Video Grid Skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-zinc-950 border border-zinc-800/50 flex flex-col">
                  {/* Thumbnail Skeleton */}
                  <div className="aspect-video bg-zinc-900 w-full relative">
                    <div className="absolute bottom-2 right-2 w-10 h-4 bg-zinc-800 rounded"></div>
                  </div>
                  {/* Meta Skeleton */}
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-zinc-800 rounded w-11/12"></div>
                    <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
                    <div className="flex items-center gap-3 pt-2">
                      <div className="h-3 bg-zinc-800 rounded w-16"></div>
                      <div className="h-3 bg-zinc-800 rounded w-12"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          /* High-Fidelity Error Card */
          <div className="max-w-md mx-auto bg-rose-950/20 border border-rose-500/30 p-6 rounded-2xl text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-rose-500 mx-auto animate-bounce" />
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-rose-200">System Connection Failed</h3>
              <p className="text-xs sm:text-sm text-rose-400/90 leading-relaxed">{error}</p>
            </div>
            <button
              onClick={() => { setError(""); setLoading(true); fetchPortalData(); }}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold transition-all"
            >
              Retry Connection
            </button>
          </div>
        ) : (
          <>
            {/* --- TAB: VIDEO PORTAL HOMEPAGE --- */}
            {activeTab === "videos" && (
              <div id="homepage-videos-panel" className="space-y-10">
                
                {/* Visual Banner Hero - Highlight Slogan and Featured Content */}
                {!searchQuery && activeCategory === "All" && featuredVideo && (
                  <div
                    id="hero-feature-billboard"
                    className="relative aspect-[21/9] w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-900 bg-slate-950 flex flex-col justify-end p-6 sm:p-10 group"
                  >
                    {/* Glowing Backdrop Banner Cover or Live Video */}
                    <div className="absolute inset-0 bg-black pointer-events-none">
                      {(settings.heroVideoId && featuredVideo) ? (
                        <iframe
                          src={`/api/iframe-proxy?url=${encodeURIComponent(featuredVideo.streamUrl || featuredVideo.videoUrl)}&dur=${encodeURIComponent(featuredVideo.duration || "")}&start=${settings.heroVideoStart || 0}&end=${settings.heroVideoEnd || 30}`}
                          className="w-full h-[150%] -translate-y-[15%] border-0 opacity-60 mix-blend-screen pointer-events-none"
                          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                          sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
                          style={{ pointerEvents: 'none' }}
                        />
                      ) : (
                        <img
                          src={getProxiedThumbnailUrl(settings.bannerUrl || (featuredVideo ? featuredVideo.thumbnailUrl : undefined), "Banner", featuredVideo ? featuredVideo.streamUrl : undefined)}
                          alt=""
                          referrerPolicy="no-referrer"
                          style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
                          className="w-full h-full object-cover opacity-30 group-hover:scale-102 transition-transform duration-1000"
                          onError={(e) => {
                            const img = e.currentTarget;
                            img.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360" fill="%230f172a"><rect width="100%" height="100%" fill="%23020617"/><text x="50%" y="50%" fill="%23334155" font-family="sans-serif" font-size="24" text-anchor="middle" dominant-baseline="middle">Banner Unavailable</text></svg>`;
                          }}
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/10 pointer-events-none"></div>
                    </div>

                    {/* Banner Slogan and Info overlays */}
                    <div className="relative space-y-3 sm:space-y-4 max-w-2xl">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30 text-[10px] font-extrabold text-green-400 tracking-widest uppercase font-mono shadow-[0_0_10px_#22c55e]">
                        <Sparkles className="w-3 h-3 text-green-400 animate-spin-slow" />
                        Alien X Feature
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h1 className="text-2xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600 uppercase tracking-tight leading-none font-sans drop-shadow-md">
                          {settings.siteName || "ALIEN X STREAM"}
                        </h1>
                        <p className="text-xs sm:text-md text-green-200/80 font-medium leading-relaxed">
                          {settings.siteSlogan || "Cosmic Streaming Interface"}
                        </p>
                      </div>

                      <div className="pt-2">
                        <motion.button
                          id="hero-play-btn"
                          onClick={() => setSelectedVideo(featuredVideo)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95, rotateX: 20, rotateY: 10 }}
                          style={{ perspective: 1000 }}
                          className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 text-black rounded-full text-xs sm:text-sm font-bold flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(34,197,94,0.5)] cursor-pointer"
                        >
                          <Play className="w-4 h-4 fill-black" />
                          <span>Watch Featured Video: {featuredVideo.title}</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Dynamic Video Gallery */}
                <div className="space-y-4">
                  <h2 className="text-lg sm:text-xl font-extrabold uppercase tracking-tight text-slate-200 font-sans border-b border-slate-900 pb-3 flex items-center gap-2">
                    <Tv className="w-5 h-5 text-rose-500" />
                    {searchQuery ? `Search Results for "${searchQuery}"` : "Premium Stream Collections"}
                  </h2>
                  <VideoGrid
                    videos={filteredVideosBySearch}
                    onVideoSelect={setSelectedVideo}
                    activeCategory={activeCategory}
                    setActiveCategory={setActiveCategory}
                    scrapers={scrapers}
                  />
                </div>
              </div>
            )}

            {/* --- TAB: SHORTS PORTRAIT FEED REMOVED --- */}

            {/* --- TAB: INTERACTIVE WEB SCRAPER VIEW --- */}
            {activeTab === "scraper" && isAdmin && (
              <div id="scraper-tab-panel">
                <ScraperView
                  scrapers={scrapers}
                  onPresetClick={handleScraperPresetClick}
                />
              </div>
            )}

            {/* --- TAB: ADMIN PANEL CONTROLLERS --- */}
            {activeTab === "admin" && (
              <div id="admin-tab-panel">
                <AdminPanel
                  isAdmin={isAdmin}
                  onLogin={handleAdminLogin}
                  videos={formattedVideos}
                  scrapers={scrapers}
                  settings={settings}
                  pages={pages}
                  onAddVideo={handleAddVideo}
                  onEditVideo={handleEditVideo}
                  onDeleteVideo={handleDeleteVideo}
                  onDeleteAllVideos={handleDeleteAllVideos}
                  onBulkDeleteVideos={handleBulkDeleteVideos}
                  onDeleteMultipleVideos={handleDeleteMultipleVideos}
                  onRefreshVideos={fetchPortalData}
                  onAddScraper={handleAddScraper}
                  onEditScraper={handleEditScraper}
                  onDeleteScraper={handleDeleteScraper}
                  onUpdateSettings={handleUpdateSettings}
                  onUpdatePage={handleUpdatePage}
                  onRenameCategory={handleRenameCategory}
                  onDeleteCategory={handleDeleteCategory}
                />
              </div>
            )}

            {/* --- TAB: CUSTOM STATIC PAGES (ABOUT / DMCA) --- */}
            {activeTab.startsWith("page-") && (
              <div id="static-page-panel" className="max-w-4xl mx-auto py-6 sm:py-12 px-4 sm:px-6 bg-slate-900/40 border border-slate-900 rounded-3xl">
                {(() => {
                  const targetSlug = activeTab.replace("page-", "");
                  const targetPage = pages.find((p) => p.id === targetSlug);
                  if (!targetPage) return <p className="text-slate-400">Page layout not found in database.</p>;
                  return (
                    <div className="space-y-6">
                      <h1 className="text-2xl sm:text-3xl font-black text-slate-100 tracking-tight border-b border-slate-800 pb-4 font-sans">
                        {targetPage.title}
                      </h1>
                      {/* Render direct HTML contents securely */}
                      <div
                        className="text-slate-300 text-sm sm:text-base leading-relaxed space-y-4 font-sans prose-styles"
                        dangerouslySetInnerHTML={{ __html: targetPage.content }}
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </main>

      {/* Cinematic Detailed Video Modal */}
      {selectedVideo && (
        <VideoDetailModal
          video={formattedVideos.find(v => v.id === selectedVideo.id) || selectedVideo}
          allVideos={formattedVideos}
          isAdmin={isAdmin}
          onClose={() => {
            setSelectedVideo(null);
            // Clear URL vid parameter from browser history silently
            window.history.pushState({}, document.title, window.location.pathname);
          }}
          onVideoSelect={setSelectedVideo}
          siteName={settings.siteName || "VIRAL STREAM"}
          watermarkText={settings.watermarkText}
          adVideoUrl={settings.adVideoUrl}
          adLinkUrl={settings.adLinkUrl}
          adEnabled={settings.adEnabled}
          adSkipSeconds={settings.adSkipSeconds}
        />
      )}

      {/* Cinematic Site Footer */}
      <footer id="app-footer" className="bg-slate-950 border-t border-slate-900 mt-12 py-10 pb-24 lg:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left space-y-1">
            <span className="text-md sm:text-lg font-black tracking-tight uppercase bg-gradient-to-r from-rose-500 to-amber-500 bg-clip-text text-transparent">
              {settings.siteName || "VIRAL STREAM"}
            </span>
            <p className="text-[10px] sm:text-xs text-slate-500 font-medium">
              © 2026 {settings.siteName || "VIRAL STREAM"}. All rights preserved. Public streams proxy.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 text-xs font-semibold text-slate-400">
            <button onClick={() => setActiveTab("videos")} className="hover:text-rose-500 transition-colors cursor-pointer">Streams</button>
            {isAdmin && <button onClick={() => setActiveTab("scraper")} className="hover:text-indigo-400 transition-colors cursor-pointer">Proxy Scraper</button>}
            {pages.map((p) => (
              <button key={p.id} onClick={() => setActiveTab(`page-${p.id}`)} className="hover:text-amber-500 transition-colors cursor-pointer">
                {p.title}
              </button>
            ))}
            {isAdmin && (
              <button onClick={() => setActiveTab("admin")} className="hover:text-slate-200 transition-colors cursor-pointer flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                Console
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Floating Mobile Search Bar */}
      {mobileSearchOpen && (
        <div className="fixed bottom-16 left-4 right-4 z-50 bg-slate-950/95 border border-slate-800 rounded-2xl p-4 shadow-2xl backdrop-blur-md animate-fadeIn lg:hidden">
          <div className="relative">
            <input
              type="text"
              placeholder="Search thousands of videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 text-slate-100 placeholder-slate-500 text-xs rounded-xl pl-9 pr-8 py-2.5 focus:outline-none focus:ring-1 focus:ring-rose-500"
              autoFocus
            />
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-2.5 text-[10px] bg-slate-800 px-2 py-0.5 rounded text-slate-400 hover:text-white"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar (Hidden when video modal is open) */}
      {!selectedVideo && (
        <div className="fixed bottom-0 left-0 right-0 z-[60] lg:hidden bg-slate-950/95 border-t border-slate-900 backdrop-blur-md px-2 py-2 flex items-center justify-around shadow-[0_-8px_30px_rgb(0,0,0,0.5)] pb-safe">
        <button
          onClick={() => {
            setActiveTab("videos");
            setSearchQuery("");
            setMobileSearchOpen(false);
            setSelectedVideo(null);
          }}
          className={`flex flex-col items-center gap-1 transition-all ${
            activeTab === "videos" ? "text-rose-500 scale-105" : "text-slate-400 hover:text-slate-200"
          }`}
        >
          <Home className="w-5 h-5" />
          <span className="text-[9px] font-bold uppercase tracking-wider font-mono">Home</span>
        </button>

        <button
          onClick={() => {
            const nextVal = !mobileSearchOpen;
            setMobileSearchOpen(nextVal);
            if (nextVal) {
              setSelectedVideo(null);
            }
          }}
          className={`flex flex-col items-center gap-1 transition-all ${
            mobileSearchOpen ? "text-amber-500 scale-105" : "text-slate-400 hover:text-slate-200"
          }`}
        >
          <Search className="w-5 h-5" />
          <span className="text-[9px] font-bold uppercase tracking-wider font-mono">Search</span>
        </button>

        {isAdmin && (
          <button
            onClick={() => {
              setActiveTab("admin");
              setMobileSearchOpen(false);
              setSelectedVideo(null);
            }}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === "admin" ? "text-emerald-400 scale-105" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <ShieldCheck className="w-5 h-5" />
            <span className="text-[9px] font-bold uppercase tracking-wider font-mono">Console</span>
          </button>
        )}

        {isAdmin && (
          <button
            onClick={() => {
              setProfileOpen(true);
              setMobileSearchOpen(false);
              setSelectedVideo(null);
            }}
            className="flex flex-col items-center gap-1 text-slate-400 hover:text-slate-200"
          >
            <User className="w-5 h-5" />
            <span className="text-[9px] font-bold uppercase tracking-wider font-mono">Profile</span>
          </button>
        )}
      </div>
      )}

      {/* Premium User Profile Drawer / Favorites Panel */}
      {profileOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden font-sans">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setProfileOpen(false)} />
          <div className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md bg-slate-950 border-l border-slate-900 flex flex-col shadow-2xl p-6 justify-between text-left">
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-900 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-rose-600 to-amber-500 flex items-center justify-center font-black text-white text-sm">
                      VS
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-white uppercase tracking-wide">My Stream Profile</h3>
                      <span className="text-[10px] font-mono text-slate-500">Premium Viewer Access</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setProfileOpen(false)}
                    className="text-slate-400 hover:text-white text-xs bg-slate-900 px-2.5 py-1 rounded"
                  >
                    Close
                  </button>
                </div>

                {/* Local Stats overview */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-800/40">
                    <span className="text-[9px] font-bold text-slate-500 uppercase font-mono tracking-wider">Gateway Latency</span>
                    <span className="text-xl font-black text-emerald-400 block mt-1">
                      0-Latency
                    </span>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-800/40">
                    <span className="text-[9px] font-bold text-slate-500 uppercase font-mono tracking-wider">Available Library</span>
                    <span className="text-xl font-black text-indigo-400 block mt-1">{videos.length}</span>
                  </div>
                </div>

                {/* Recent Uploads List */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider font-mono">Recent Uploads</h4>
                  {videos.length > 0 ? (
                    <div className="space-y-2 overflow-y-auto max-h-[30vh]">
                      {videos.slice(0, 5).map((video) => (
                        <div
                          key={video.id}
                          onClick={() => {
                            setSelectedVideo(video);
                            setProfileOpen(false);
                          }}
                          className="flex items-center gap-3 p-2 bg-slate-900/40 hover:bg-slate-900 border border-slate-900 rounded-xl cursor-pointer transition-colors"
                        >
                          <img
                            src={getProxiedThumbnailUrl(video.thumbnailUrl, video.title, video.streamUrl)}
                            alt=""
                            referrerPolicy="no-referrer"
                            style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
                            className="w-12 h-12 object-cover rounded-lg border border-slate-800/60 flex-shrink-0"
                            onError={(e) => {
                              const img = e.currentTarget;
                              img.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360" fill="%230f172a"><rect width="100%" height="100%" fill="%23020617"/><text x="50%" y="50%" fill="%23334155" font-family="sans-serif" font-size="10" text-anchor="middle" dominant-baseline="middle">N/A</text></svg>`;
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <span className="text-[10px] text-rose-500 font-bold uppercase font-mono">{video.category}</span>
                            <p className="text-xs font-bold text-slate-200 line-clamp-1">{video.title}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 font-medium py-4 text-center border border-dashed border-slate-900 rounded-xl">
                      No videos available.
                    </div>
                  )}
                </div>

                {/* System Telemetry Proxy */}
                <div className="p-4 bg-slate-950 border border-slate-900 rounded-2xl space-y-2.5">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider font-mono block">Node Connection Telemetry</span>
                  <div className="flex justify-between text-xs text-slate-400 font-mono">
                    <span>Server Status</span>
                    <span className="text-emerald-400 font-bold">ONLINE</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 font-mono">
                    <span>Ingestion Gateway</span>
                    <span className="text-indigo-400">REST API</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 font-mono">
                    <span>IP Proxy Routing</span>
                    <span className="text-slate-400">ACTIVE</span>
                  </div>
                </div>
              </div>

              {isAdmin && (
                <div className="border-t border-slate-900 pt-4 text-center">
                  <button
                    onClick={() => {
                      setActiveTab("admin");
                      setProfileOpen(false);
                    }}
                    className="w-full py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition-colors"
                  >
                    Enter Admin Console
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Removed DevTools Active Guard Block Screen */}

      {/* Security Toast Notification Banner */}
      {toastMessage && (
        <div id="security-toast-alert" className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[99999] bg-slate-900/95 backdrop-blur border border-rose-500/30 text-rose-400 px-5 py-3 rounded-2xl text-xs sm:text-sm font-bold shadow-xl shadow-rose-950/35 flex items-center gap-2.5 animate-bounce select-none font-sans">
          <ShieldAlert className="w-4 h-4 text-rose-500" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Secure Admin Passcode Authentication Dialog */}
      {authModalOpen && (
        <div id="admin-passcode-dialog" className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm font-sans">
          <div className="max-w-sm w-full bg-zinc-900/95 border border-green-500/50 rounded-3xl p-6 sm:p-8 space-y-6 shadow-[0_0_30px_rgba(34,197,94,0.3)] relative">
            <div className="w-12 h-12 bg-green-500/10 border border-green-500/50 rounded-2xl flex items-center justify-center text-green-400 mx-auto">
              <Lock className="w-6 h-6 drop-shadow-[0_0_5px_#22c55e]" />
            </div>
            
            <div className="text-center space-y-1.5">
              <h2 className="text-lg font-black text-slate-100 tracking-tight drop-shadow-[0_0_5px_#22c55e]">Omnitrix Console Access</h2>
              <p className="text-xs text-green-400/80">
                Please enter the security password to unlock administrator capabilities.
              </p>
            </div>

            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                setAuthError("");
                const success = await handleAdminLogin(authPassword);
                if (success) {
                  setAuthModalOpen(false);
                  setAuthPassword("");
                  setActiveTab("admin");
                } else {
                  setAuthError("Incorrect admin security passcode.");
                }
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <input
                  id="admin-security-passcode-input"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-green-500 rounded-xl px-4 py-3 text-center text-slate-100 placeholder-zinc-700 font-mono tracking-widest text-lg focus:outline-none transition-colors shadow-[inset_0_0_10px_rgba(34,197,94,0.1)]"
                />
                {authError && (
                  <p className="text-rose-500 text-[11px] font-semibold text-center font-sans">
                    {authError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setAuthModalOpen(false);
                    setAuthPassword("");
                    setAuthError("");
                  }}
                  className="w-full py-2.5 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-green-400 hover:border-green-500/50 rounded-xl text-xs font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-zinc-950 rounded-xl text-xs font-black shadow-[0_0_15px_#22c55e] hover:brightness-125 transition-all"
                >
                  Unlock Portal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
